/*
 * Zardo card image fit (same method as the live theme's cart-drawer script)
 *
 * Product photos are not cropped consistently: some files are tight to the product,
 * others are canvases with wide transparent margins, so the same frame makes one
 * product look large and the next one small.
 *
 * For each card image this reads a small copy of the file (Shopify's CDN allows
 * CORS), finds the box of visible (non-transparent) pixels, then scales and centres
 * the image with the CSS `scale` / `translate` properties so the visible product
 * fills its frame (contain, never cropped, at most 1.6x). Those properties compose
 * with the card's 3D tilt `transform`. Photos with an opaque background, or already
 * tight to the product, are left as they are.
 *
 * Load events are caught at the document level and new nodes are watched, so cards
 * added later (load more, carousels, search, filters) are handled without re-initialising.
 */
(() => {
  if (window.zardoCardFitReady) return;
  window.zardoCardFitReady = true;

  const IMG = '.zardo-card__img, #search-drawer .zardo-search-product-card__media img, #search-drawer .predictive-search-result__media img';
  const SAMPLE_WIDTH = 120;
  const cache = new Map();

  const measure = (src) => {
    let url;
    try { url = new URL(src, location.href); } catch (error) { return Promise.resolve(null); }
    url.searchParams.set('width', String(SAMPLE_WIDTH));
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    const key = url.href;
    if (cache.has(key)) return cache.get(key);

    const promise = new Promise((resolve) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = () => {
        try {
          const w = probe.naturalWidth;
          const h = probe.naturalHeight;
          if (!w || !h) return resolve(null);
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(probe, 0, 0);
          const { data } = context.getImageData(0, 0, w, h);
          const alpha = (x, y) => data[(y * w + x) * 4 + 3];
          // Opaque corners: a photo with a background, nothing to trim.
          if ([alpha(0, 0), alpha(w - 1, 0), alpha(0, h - 1), alpha(w - 1, h - 1)].some((a) => a > 12)) return resolve(null);
          let x0 = w, y0 = h, x1 = -1, y1 = -1;
          for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
              if (data[(y * w + x) * 4 + 3] > 24) {
                if (x < x0) x0 = x;
                if (x > x1) x1 = x;
                if (y < y0) y0 = y;
                if (y > y1) y1 = y;
              }
            }
          }
          resolve(x1 < 0 ? null : { x0: x0 / w, y0: y0 / h, x1: (x1 + 1) / w, y1: (y1 + 1) / h, ratio: w / h });
        } catch (error) {
          resolve(null);
        }
      };
      probe.onerror = () => resolve(null);
      probe.src = key;
    });

    cache.set(key, promise);
    return promise;
  };

  const reset = (img) => {
    img.style.removeProperty('scale');
    img.style.removeProperty('translate');
  };

  // The frame changes with the grid (1, 2, 3 per row) and the viewport: refit on every size change.
  const resized = typeof ResizeObserver === 'function'
    ? new ResizeObserver((entries) => entries.forEach((entry) => fit(entry.target)))
    : null;

  const fit = async (img) => {
    if (resized && !img._zardoFitObserved) {
      img._zardoFitObserved = true;
      resized.observe(img);
    }
    const src = img.currentSrc || img.src;
    if (!src || !img.complete) return;
    const m = await measure(src);
    const bw = img.offsetWidth;
    const bh = img.offsetHeight;
    if (!m || !bw || !bh) return reset(img);
    const fitMode = getComputedStyle(img).objectFit;
    if (fitMode === 'cover') return reset(img);

    // Rendered size of the whole file inside the element.
    let rw = bw;
    let rh = bh;
    if (fitMode === 'contain' || fitMode === 'scale-down') {
      if (bw / bh > m.ratio) rw = bh * m.ratio;
      else rh = bw / m.ratio;
    }
    const ox = (bw - rw) / 2;
    const oy = (bh - rh) / 2;
    const cx0 = ox + m.x0 * rw;
    const cx1 = ox + m.x1 * rw;
    const cy0 = oy + m.y0 * rh;
    const cy1 = oy + m.y1 * rh;
    const scale = Math.min(1.6, bw / (cx1 - cx0), bh / (cy1 - cy0));
    if (!(scale > 1.03)) return reset(img);

    const shiftX = -((cx0 + cx1) / 2 - bw / 2) * scale;
    const shiftY = -((cy0 + cy1) / 2 - bh / 2) * scale;
    img.style.setProperty('scale', scale.toFixed(3));
    img.style.setProperty('translate', `${(shiftX / bw * 100).toFixed(2)}% ${(shiftY / bh * 100).toFixed(2)}%`);
  };

  const fitAll = (root = document) => root.querySelectorAll(IMG).forEach(fit);

  document.addEventListener('load', (event) => {
    if (event.target instanceof HTMLImageElement && event.target.matches(IMG)) fit(event.target);
  }, true);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (node.matches(IMG)) fit(node);
      else node.querySelectorAll(IMG).forEach(fit);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => fitAll(), 200);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => fitAll());
  else fitAll();
})();
